import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import apiClient from '@/services/api';

export interface ReportFilters {
  restaurants: ReturnType<typeof apiClient.getRestaurants> extends Promise<infer R>
    ? R extends { content: infer T }
      ? T
      : never
    : never;
  selectedRestaurantId: number | null;
  setSelectedRestaurantId: (id: number | null) => void;
  startDate: string;
  setStartDate: (d: string) => void;
  endDate: string;
  setEndDate: (d: string) => void;
  currency: string;
  formatCurrency: (value: number | string) => string;
  selectedRestaurant: { id: number; name: string; currency?: string } | undefined;
}

/**
 * Shared filter state for the report sub-pages.
 * Loads the restaurant list, picks the first one as the default, and exposes a
 * currency formatter bound to the selected restaurant's currency.
 */
export function useReportFilters(defaultDays = 0) {
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), defaultDays), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Walk every page instead of hardcoding a page size, so a platform with
  // more restaurants than that cap doesn't silently lose the dropdown's tail.
  const { data: restaurants = [] } = useQuery({
    queryKey: ['restaurants-all'],
    queryFn: async () => {
      const all: ReportFilters['restaurants'] = [];
      let page = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const result = await apiClient.getRestaurants(page, 200);
        all.push(...result.content);
        if (result.last) break;
        page += 1;
      }
      return all;
    },
  });

  useEffect(() => {
    if (!selectedRestaurantId && restaurants.length > 0) {
      setSelectedRestaurantId(restaurants[0].id);
    }
  }, [restaurants, selectedRestaurantId]);

  const selectedRestaurant = restaurants.find((r) => r.id === selectedRestaurantId);
  const currency = selectedRestaurant?.currency || 'USD';

  const formatCurrency = useMemo(() => {
    return (value: number | string) => {
      const n = typeof value === 'string' ? Number(value) : value;
      if (!Number.isFinite(n)) return '—';
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
      } catch {
        return `${currency} ${Number(n).toFixed(2)}`;
      }
    };
  }, [currency]);

  return {
    restaurants,
    selectedRestaurantId,
    setSelectedRestaurantId,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    currency,
    formatCurrency,
    selectedRestaurant,
  };
}
